import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { settingsApi } from '../../../api/endpoints/settings';
import { Toggle } from '../../../components/ui/Toggle';
import { Input } from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';
import { useToast } from '../../../components/ui/Alert';
import type { StoreSettings } from '../../../types/settings';

const PAYMENT_METHODS = ['bank_transfer'] as const;

interface Props { settings?: StoreSettings; storeId: string; }

export function PaymentMethodsTab({ settings, storeId }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [methods, setMethods] = useState<Record<string, { enabled: boolean }>>({});
  const [bankHolder, setBankHolder] = useState('');
  const [bankName, setBankName] = useState('');
  const [bankIban, setBankIban] = useState('');
  // EdfaPay / TLYNC (external gateway) — only available when the platform admin has allowed it.
  const [edfaEnabled, setEdfaEnabled] = useState(false);
  const [edfaStoreId, setEdfaStoreId] = useState('');
  const [edfaToken, setEdfaToken] = useState('');
  const [edfaTest, setEdfaTest] = useState(false);

  const showEdfa = !!settings?.allowExternalPaymentGateways;

  useEffect(() => {
    if (settings?.paymentMethods) {
      const m: Record<string, { enabled: boolean }> = {};
      for (const key of PAYMENT_METHODS) {
        m[key] = { enabled: !!(settings.paymentMethods[key]?.enabled) };
      }
      setMethods(m);
      const bt = (settings.paymentMethods.manualTransfer ?? settings.paymentMethods.bank_transfer) as any;
      if (bt) {
        setBankHolder(bt.accountHolder ?? bt.holderName ?? '');
        setBankName(bt.bankName ?? '');
        setBankIban(bt.iban ?? bt.accountNumber ?? '');
      }
      const edfa = settings.paymentMethods.edfapay as any;
      if (edfa) {
        setEdfaEnabled(!!edfa.enabled);
        setEdfaStoreId(edfa.config?.merchantId ?? '');
        setEdfaToken(edfa.config?.token ?? '');
        setEdfaTest(edfa.config?.environment === 'sandbox');
      }
    }
  }, [settings]);

  const mutation = useMutation({
    mutationFn: () => {
      // The backend REPLACES the whole paymentMethods object, so start from the
      // existing config and overlay our edits — otherwise other gateways get wiped.
      const existing = (settings?.paymentMethods ?? {}) as Record<string, any>;
      const payload: Record<string, any> = {
        ...existing,
        ...methods,
        manualTransfer: {
          ...(existing.manualTransfer ?? {}),
          ...methods.bank_transfer,
          accountHolder: bankHolder || undefined,
          bankName: bankName || undefined,
          iban: bankIban || undefined,
        },
      };
      if (showEdfa) {
        payload.edfapay = {
          enabled: edfaEnabled,
          config: {
            merchantId: edfaStoreId || undefined,
            token: edfaToken || undefined,
            environment: edfaTest ? 'sandbox' : 'production',
          },
        };
      }
      return settingsApi.updatePaymentMethods(storeId, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', storeId] });
      toast('success', t('common.success'));
    },
    onError: () => toast('error', t('common.error')),
  });

  const toggleMethod = (key: string) => {
    setMethods((prev) => ({
      ...prev,
      [key]: { ...prev[key], enabled: !prev[key]?.enabled },
    }));
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
        <h2 className="text-base font-semibold">{t('settings.paymentMethods')}</h2>
        <p className="text-xs text-text-muted -mt-2">
          {t('settings.paymentsAutomaticDesc', 'Card, mada, Apple Pay, and STC Pay are handled automatically by the platform. Enable bank transfer below if you want to accept manual transfers too.')}
        </p>
        {PAYMENT_METHODS.map((method) => (
          <div key={method} className="flex items-center justify-between py-3 border-b border-border-default/50 last:border-0">
            <div>
              <p className="text-sm font-medium">{t(`settings.payment_${method}`, method)}</p>
            </div>
            <Toggle
              checked={methods[method]?.enabled ?? false}
              onChange={() => toggleMethod(method)}
            />
          </div>
        ))}
      </section>

      {/* Bank Transfer Account Details */}
      {methods.bank_transfer?.enabled && (
        <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
          <h2 className="text-base font-semibold mb-4">{t('settings.bankAccountDetails', 'Bank Account Details')}</h2>
          <p className="text-xs text-text-muted -mt-2">{t('settings.bankAccountDesc', 'Customers will see these details when choosing bank transfer.')}</p>
          <Input
            label={t('settings.accountHolder', 'Account Holder Name')}
            value={bankHolder}
            onChange={(e) => setBankHolder(e.target.value)}
          />
          <Input
            label={t('settings.bankName', 'Bank Name')}
            value={bankName}
            onChange={(e) => setBankName(e.target.value)}
          />
          <Input
            label={t('settings.iban', 'IBAN / Account Number')}
            value={bankIban}
            onChange={(e) => setBankIban(e.target.value)}
            className="[direction:ltr]"
          />
        </section>
      )}

      {/* EdfaPay — external gateway, shown only when the admin has allowed this store */}
      {showEdfa && (
        <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold">{t('settings.edfapay', 'EdfaPay')}</h2>
              <p className="text-xs text-text-muted mt-0.5">
                {t('settings.edfapayDesc', 'Accept card payments through your own EdfaPay account. Funds settle directly to you, in your store currency.')}
              </p>
            </div>
            <Toggle checked={edfaEnabled} onChange={() => setEdfaEnabled((v) => !v)} />
          </div>
          {edfaEnabled && (
            <div className="space-y-4 pt-2">
              <Input
                label={t('settings.edfaStoreId', 'TLYNC Store ID')}
                value={edfaStoreId}
                onChange={(e) => setEdfaStoreId(e.target.value)}
                placeholder={t('settings.edfaStoreIdHint', 'The long store id from Store Details')}
                className="[direction:ltr]"
              />
              <Input
                label={t('settings.edfaToken', 'Access Token')}
                type="password"
                value={edfaToken}
                onChange={(e) => setEdfaToken(e.target.value)}
                className="[direction:ltr]"
              />
              <div className="flex items-center justify-between pt-1">
                <div>
                  <p className="text-sm font-medium">{t('settings.edfaTestMode', 'Test mode (UAT)')}</p>
                  <p className="text-xs text-text-muted mt-0.5">{t('settings.edfaTestModeDesc', 'Use the TLYNC test environment — no real funds move.')}</p>
                </div>
                <Toggle checked={edfaTest} onChange={() => setEdfaTest((v) => !v)} />
              </div>
            </div>
          )}
        </section>
      )}

      <div className="flex justify-end">
        <Button onClick={() => mutation.mutate()} isLoading={mutation.isPending}>{t('common.save')}</Button>
      </div>
    </div>
  );
}
